// ============================================
// UPDATED: SimpleScrollList.jsx
// Enhanced with rolling window focus preservation
// PERFORMANCE OPTIMIZED: Per-item memoization instead of whole-list memoization
// FIXED: Now reacts to isShiftingWindow changes to unlock scroll
// ============================================
//
// ARCHITECTURE NOTES:
// - This is the DEFAULT vertical list used by TourParticipant
// - Uses browser native scrolling (overflow-y: auto)
// - Does NOT use VirtualizedGrid (that's only for SmoothScrollList with >100 items)
// - Works with SmartEventList rolling window (maintains ~15 items at a time)
//
// PERFORMANCE OPTIMIZATIONS IMPLEMENTED:
// ✅ Throttled scroll detection (100ms = 10fps)
// ✅ Debounced parent notifications (200ms after scroll settles)
// ✅ requestAnimationFrame for DOM measurements to avoid layout thrashing
// ✅ Duplicate index filtering to prevent unnecessary parent re-renders
// ✅ Guards to skip work during programmatic scroll adjustments
// ✅ Passive event listeners for smooth scrolling on mobile
// ✅ Per-item memoization (not whole-list) for better re-render behavior
//
// ============================================

import React, { useState, useEffect, useLayoutEffect, createRef, useRef, useCallback } from 'react';
import { CircularProgress, Grid, Box, Typography } from '@mui/material';
import PlaceDetails from '../PlaceDetails/PlaceDetails';
import { styled } from '@mui/system';
import axios from 'axios';

/**
 * Verbose scroll tracing. Flip to true when debugging list behaviour.
 *
 * These logs fired on every throttled scroll tick and on every window shift,
 * building fresh objects and arrays each time. `DEBUG && console.log(...)`
 * short-circuits before any of that work happens, so leaving them in the file
 * costs nothing at runtime.
 */
const DEBUG = false;

/**
 * Focused scroll instrumentation, deliberately separate from DEBUG.
 *
 * Every line is prefixed `[VSCROLL]` and fits on one line, so the console can
 * be filtered down to just the scroll story with no other noise. Set to false
 * once the behaviour is settled.
 */
const TRACE = true;
const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
const trace = (...args) => {
  if (!TRACE) return;
  const ms = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0);
  console.log('[VSCROLL]', String(ms).padStart(6), ...args);
};

// ============================================
// PERFORMANCE UTILITIES
// ============================================

/**
 * Inline debounce utility - delays function execution until after wait time has elapsed
 * since the last call. Used to reduce parent component re-renders.
 */
function debounce(fn, delay) {
  let timer;
  return function(...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * Inline throttle utility - ensures function runs at most once per limit period.
 * Used to reduce scroll handler frequency.
 */
function throttle(fn, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      fn.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Set scrollTop *immediately*, and report what actually happened.
 *
 * BUGFIX (compensation wrote scrollTop but nothing moved): the container used
 * to carry CSS `scroll-behavior: smooth`. Per CSSOM-View, assigning `scrollTop`
 * performs a scroll using the element's scroll-behavior — so with `smooth` the
 * assignment starts an ANIMATION and an immediate read-back returns the old
 * value. That is precisely the trace line
 *
 *   compensate/anchor removeTop id=review_100 drift=-480 scrollTop 13890 -> 13890
 *
 * It was never a clamp: the target (13410) was *below* the current position, so
 * a reduced max scroll could only have moved it further down, not left it
 * identical. Worse, the animation kept running for hundreds of ms after
 * `scrollAdjustmentInProgressRef` was cleared on the next frame, so its tail
 * was indistinguishable from the user scrolling.
 *
 * Forcing `auto` for the duration of the write makes compensation synchronous
 * and measurable. The returned info also surfaces genuine clamping (the browser
 * refusing to scroll past `scrollHeight - clientHeight`), which is a real
 * failure mode whenever content height has just shrunk.
 */
function setScrollTopInstant(container, target) {
  const previousBehavior = container.style.scrollBehavior;
  container.style.scrollBehavior = 'auto';

  const before = container.scrollTop;
  const maxScroll = Math.max(0, container.scrollHeight - container.clientHeight);
  const wanted = target;
  const clampedTarget = Math.max(0, Math.min(wanted, maxScroll));

  container.scrollTop = clampedTarget;
  const after = container.scrollTop;

  container.style.scrollBehavior = previousBehavior;

  return {
    before,
    after,
    wanted,
    maxScroll,
    // True when the scroll extent could not accommodate the correction — i.e.
    // the content is now too short to hold the position we needed.
    clamped: Math.abs(wanted - clampedTarget) > 0.5,
    applied: after - before,
  };
}


// ============================================
// MEMOIZED ITEM COMPONENT
// ============================================

/**
 * Memoized vertical event row - only re-renders when its props change
 * This is key to maintaining performance after removing whole-list memoization
 */
const VerticalEventRow = React.memo(function VerticalEventRow({
  place,
  index,
  isActive,
  elRef,
  onClick,
  language,
  onLocationClick,
  suppressAutoScroll,
  isShiftingWindow,
  onExpand,
  onCreatorClick,
}) {
  if (place?.__skeleton) {
    return (
      <Grid
        ref={elRef}
        key={`skeleton-${index}`}
        item
        xs={12}
      >
        <div style={{
          width: '100%',
          height: 96,
          borderRadius: 8,
          boxShadow: '0 1px 3px rgba(0,0,0,.12)',
          background: 'linear-gradient(90deg, #f2f2f2 25%, #e6e6e6 50%, #f2f2f2 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite'
        }} />
      </Grid>
    );
  }

  return (
    <Grid
      ref={elRef}
      key={place.event_id || place.id || `place-${index}`}
      item
      xs={12}
      data-event-id={place.id || place.event_id}
      // Identifies THIS copy of the row. Replayed items reuse event ids, so
      // this is what the scroll anchor matches on. Must stay in sync with the
      // key used by anchorIdRef in findCenteredCard.
      data-render-key={place.__renderKey || place.event_id || place.id}
      onClick={() => onClick(place, index)}
    >
      <PlaceDetails
        place={place}
        selected={isActive}
        refProp={elRef}
        onLocationClick={onLocationClick}
        language={language}
        suppressAutoScroll={suppressAutoScroll}
        isShiftingWindow={isShiftingWindow}
        onExpand={onExpand}
        hideParticipation={true}
        onCreatorClick={onCreatorClick}
      />
    </Grid>
  );
});

const StyledContainer = styled(Box)({
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  padding: '8px 5px 15px 15px',
  boxSizing: 'border-box',
  overflow: 'hidden',
  position: 'relative',
});

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  width: '100%',
});

const ScrollableGrid = styled(Grid)(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingRight: '2px',

  // INSTAGRAM-STYLE: Mobile scroll optimizations
  WebkitOverflowScrolling: 'touch', // iOS momentum scrolling
  overscrollBehavior: 'contain', // Prevent overscroll bounce & scroll chaining

  // BUGFIX (double compensation): browser-native scroll anchoring was running
  // ALONGSIDE this component's own anchor correction, so a single displacement
  // had two independent correctors — the browser silently adjusting scrollTop
  // for content changes above the viewport, and the layout effect measuring
  // drift and adjusting it again. They do not agree on the anchor element, and
  // the browser's adjustment lands between our before/after measurements, which
  // corrupts the drift we compute.
  //
  // We do this precisely and on a known element, so opt the browser out.
  overflowAnchor: 'none',

  // GPU acceleration for smooth scrolling
  transform: 'translateZ(0)',
  willChange: 'scroll-position',

  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: 'rgba(0,0,0,0.4)',
  },
}));

const emptyMessages = {
  TH: 'ไม่พบกิจกรรม',
  EN: 'No events found matching your criteria.',
  CN: '未找到符合条件的活动'
};

// Time window to clamp focus after a shift (ms)
const POST_SHIFT_CLAMP_MS = 500; // Extended to ensure we don't miss the window

// Minimum accuracy threshold for scroll compensation
const MIN_COMPENSATION_ACCURACY = 0.85; // tweakable

const SimpleScrollList = ({
  places,
  childClicked,
  isLoading,
  onLocationClick,
  language = 'TH',
  onActiveIndexChange,
  hasSearched = true,
  currentUser = null,
  tracker = null,
  listRef: externalListRef = null,
  pendingScrollAdjustment = null,
  isShiftingWindow = false, // NEW: Prevent focus detection during window shifts
  isLoadingNext = false, // NEW: Prevent focus during next loading
  isLoadingPrevious = false, // NEW: Prevent focus during previous loading
  onScrollStart = null, // PERFORMANCE: Callback when user starts scrolling
  onScrollEnd = null, // PERFORMANCE: Callback when user stops scrolling
  preserveScrollOnPrepend = true, // NEW: Controls automatic scroll compensation after prepend
  // NEW: Authoritative focus anchoring
  authoritativeFocusIndex = null,
  authoritativeFocusEventId = null,
  // NEW: Edge-demand channel - separate from focus for reliable loading
  onEdgeDemand = null,
  // NEW: Post-shift anchor - clamps focus for a short time after shift
  postShiftAnchorIndex = null,
  postShiftAnchorEventId = null,
  postShiftAnchorAt = null,
  postShiftAnchorAuthoritative = false, // NEW: Whether anchor is strong (vs weak fallback)
  // STABILIZATION: Suppress child auto-scroll during window shifts
  suppressAutoScroll = false,
  // NEW: Post-shift silence window duration (ms) - prevents scroll-based focus override after shift
  postShiftSuppressMs = 500,
  // NEW: Force authoritative anchor from parent (Part 2)
  forceAuthoritativeAnchor = false,
  // Expand callback — passed down to PlaceDetails
  onExpand = null,
  onCreatorClick = null,
}) => {
  // Debug log to confirm re-renders
  DEBUG && console.log('[SimpleScrollList] render. isShiftingWindow =', isShiftingWindow);

  const [elRefs, setElRefs] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const scrollContainerRef = useRef(null);
  const isScrollingFromClickRef = useRef(false);
  const lastChildClickedRef = useRef(null);
  // False until the mount-time position restore has run. See the effect below.
  const initialRestoreDoneRef = useRef(false);
  const lastReportedIndexRef = useRef(-1);
  const itemHeightCacheRef = useRef(new Map()); // eventId -> measured height
  const lastMeasurementTimeRef = useRef(0); // Throttle measurements
  const previousItemsRef = useRef([]); // Track previous items to detect what was removed
  const scrollAdjustmentInProgressRef = useRef(false);

  const lastScrollTopRef = useRef(0);
  const lastAppliedAdjustmentIdRef = useRef(null);
  const lastHeightUpdateRef = useRef(0);
  const windowShiftCooldownRef = useRef(false);
  const userScrollingRef = useRef(false);
  const dwellTimerRef = useRef(null);
  const lastItemsSignatureRef = useRef(''); // Track items signature to avoid unnecessary measurements
  const lastEdgeDemandTimeRef = useRef(0); // Cooldown for edge demand to prevent spam
  /**
   * Edge hysteresis, mirroring SimpleHorizontalList's edgeStateRef.
   * 'down' | 'up' | null. An edge may request data only when the scroll
   * position *enters* its zone; it re-arms once the position returns to the
   * middle. See the long note at the call site in handleScroll.
   */
  const edgeStateRef = useRef(null);
  // Scroll geometry captured at the moment we last asked for data, used to tell
  // "the last request achieved nothing" from "we've moved on".
  const lastDemandHeightRef = useRef(0);
  const lastDemandTopRef = useRef(0);
  /**
   * If we are still sitting in the same edge zone this long after the last
   * request, assume that request was fruitless and try once more. This is what
   * makes the end of the feed un-stickable: worst case it retries at well under
   * one request per second, which is a trickle, not a runaway.
   */
  const STUCK_RETRY_MS = 1200;

  /**
   * Scroll anchor — the identity and viewport-relative position of the topmost
   * visible row, refreshed on every scroll pass.
   *
   * This is the basis of the compensation redesign. Summing cached/estimated
   * item heights can only ever approximate how far the content moved, and for
   * rows that have never been rendered there is no height to look up at all —
   * so a 15-row shift accumulated a large error, the viewport landed somewhere
   * unintended, and that unintended position became a new "centred" card. That
   * is the visible jump. Measuring one known element before and after the DOM
   * update gives the exact number instead, with no estimation anywhere.
   */
  const anchorIdRef = useRef(null);
  const anchorTopRef = useRef(0);
  /**
   * scrollTop at the instant the anchor position above was captured.
   *
   * BUGFIX (compensation cancelled the user's own gesture): `anchorTopRef` is
   * refreshed by findCenteredCard, which is throttled and rAF-deferred, so by
   * the time a shift commits the user has usually scrolled further. Comparing
   * the anchor's new offset against that stale value counts the user's own
   * movement as "drift" and dutifully undoes it — the trace shows exactly that,
   * a +152px gesture followed by a -151px "compensation" back to where it
   * started, which on top of everything else is why the list felt stuck.
   *
   * Recording the scroll position alongside the anchor lets us predict where
   * the anchor *should* be at the current scrollTop, so drift measures only
   * what the DOM change actually did.
   */
  const anchorScrollTopRef = useRef(0);

  // INSTAGRAM-STYLE: Scroll velocity tracking for dynamic prefetching
  const scrollVelocityRef = useRef(0); // pixels per millisecond
  const lastScrollTimeRef = useRef(0); // timestamp of last scroll event

  /**
   * Timestamp until which we treat scrolling as user-driven.
   *
   * BUGFIX (runaway load loop): edge demand used to be derived purely from
   * scroll position, with no notion of *who* caused the scroll. Every scroll
   * the list caused itself — the mount restore, and above all the scrollTop
   * writes from prepend/removeTop compensation — looked exactly like the user
   * scrolling to an edge, so it requested another batch, which shifted the
   * window, which moved scrollTop, which requested another batch. The console
   * trace shows this clearly: every setState originates from
   * findCenteredCard → onEdgeDemand → loadNext/loadPrevious, never from a
   * gesture.
   *
   * Prefetching is only ever a response to the user moving, so it is now gated
   * on a real input event having happened recently. Programmatic scrolls set
   * no intent and therefore cannot feed themselves.
   */
  const userIntentUntilRef = useRef(0);
  const USER_INTENT_WINDOW_MS = 1500;
  const hasUserInteractedRef = useRef(false);

  useEffect(() => {
    return () => {
      if (tracker) {
        tracker.trackSessionEnd('scroll_list_unmount');
      }
      if (dwellTimerRef.current) clearTimeout(dwellTimerRef.current);
    };
  }, [tracker]);

  /**
   * Record genuine user input on the scroll container. Only these events count
   * as intent — `scroll` deliberately does not, because it fires for
   * programmatic scrollTop writes too and that is the loop we are breaking.
   */
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return undefined;

    const claimIntent = () => {
      userIntentUntilRef.current = Date.now() + USER_INTENT_WINDOW_MS;
      hasUserInteractedRef.current = true;
    };

    // Continuous gestures also re-run edge detection directly. At the very
    // bottom scrollTop is pinned, so no `scroll` events are emitted and the
    // scroll-driven detector never runs again — but wheel/touchmove still fire.
    // This is what keeps the feed extendable when the user is already at the end.
    const claimIntentAndCheck = () => {
      claimIntent();
      requestEdgeRef.current?.();
    };

    container.addEventListener('pointerdown', claimIntent, { passive: true });
    container.addEventListener('touchstart', claimIntent, { passive: true });
    container.addEventListener('keydown', claimIntent, { passive: true });
    container.addEventListener('touchmove', claimIntentAndCheck, { passive: true });
    container.addEventListener('wheel', claimIntentAndCheck, { passive: true });

    return () => {
      container.removeEventListener('pointerdown', claimIntent);
      container.removeEventListener('touchstart', claimIntent);
      container.removeEventListener('keydown', claimIntent);
      container.removeEventListener('touchmove', claimIntentAndCheck);
      container.removeEventListener('wheel', claimIntentAndCheck);
    };
  }, [isLoading, places]);


  // Create refs for all items
  useEffect(() => {
    const placesCount = Array.isArray(places) ? places.length : 0;
    setElRefs((refs) => {
      if (refs.length === placesCount) return refs;
      return Array(placesCount).fill().map((_, i) => refs[i] || createRef());
    });
  }, [places?.length]);

  // Track previous items for compensation
  useEffect(() => {
    if (Array.isArray(places) && places.length > 0) {
      previousItemsRef.current = places;
    }
  }, [places]);

  /**
   * CRITICAL FIX: SYNCHRONOUS height measurement before DOM updates
   * Runs in useLayoutEffect to measure BEFORE items are removed
   * This prevents the repositioning bug where removed items have no cached height
   */
  useLayoutEffect(() => {
    if (!Array.isArray(places) || places.length === 0) return;
    if (!scrollContainerRef.current) return;

    // SYNCHRONOUS measurement - run immediately before DOM updates
    places.forEach((place, index) => {
      if (place?.__skeleton) return;

      const eventId = String(place.event_id || place.id);
      if (!eventId || itemHeightCacheRef.current.has(eventId)) return;

      const ref = elRefs[index];
      if (ref?.current) {
        const rect = ref.current.getBoundingClientRect();
        if (rect.height > 0) {
          itemHeightCacheRef.current.set(eventId, rect.height);
        }
      }
    });
  }, [places, elRefs]);

  /**
   * Track window shift for scroll detection (but don't lock scroll)
   * CRITICAL FIX: Removed aggressive scroll locking that was causing stuck scrolling
   * The scroll adjustment flag is sufficient to prevent interference
   */
  useEffect(() => {
    if (isShiftingWindow) {
      trace('SHIFT start  scrollTop=' + Math.round(scrollContainerRef.current?.scrollTop ?? -1),
        'anchor=' + anchorIdRef.current,
        '@' + Math.round(anchorTopRef.current),
        'capturedAt=' + Math.round(anchorScrollTopRef.current));
      scrollAdjustmentInProgressRef.current = true;
      windowShiftCooldownRef.current = true;

      // INSTAGRAM-STYLE: Very short delay - minimize "dead zones"
      const clearTimer = setTimeout(() => {
        scrollAdjustmentInProgressRef.current = false;
        DEBUG && console.log('[SimpleScrollList] ✅ Scroll adjustment flag cleared');
      }, 30); // Reduced from 300ms to 30ms

      return () => clearTimeout(clearTimer);
    }

    // BUGFIX: windowShiftCooldownRef existed but was never assigned anywhere,
    // so every guard that consulted it was dead code. The rail has exactly this
    // cooldown (400ms after a shift, ignore edge demand) and it is a large part
    // of why the rail stays calm: it gives scroll compensation time to land
    // before the list is allowed to react to its own new scroll position.
    // `h` is the whole point of this line: a collapse in scrollable height is
    // what "the list won't scroll any further" actually is, so it must be
    // visible in the trace right next to the row count that caused it.
    trace('SHIFT end    scrollTop=' + Math.round(scrollContainerRef.current?.scrollTop ?? -1),
      'items=' + (Array.isArray(places) ? places.length : -1),
      'h=' + Math.round(scrollContainerRef.current?.scrollHeight ?? -1),
      'maxScroll=' + Math.round(Math.max(0,
        (scrollContainerRef.current?.scrollHeight ?? 0) - (scrollContainerRef.current?.clientHeight ?? 0))));
    windowShiftCooldownRef.current = true;
    const cooldownTimer = setTimeout(() => {
      windowShiftCooldownRef.current = false;
      trace('cooldown end');
    }, 400);

    return () => clearTimeout(cooldownTimer);
    // `places` is read only for the trace line above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShiftingWindow]);


  /**
   * SIMPLIFIED: Scroll compensation with height map
   * Always compensates for removeTop using cached heights
   */
  useLayoutEffect(() => {
    if (!pendingScrollAdjustment || !scrollContainerRef.current) {
      return;
    }

    // Prevent duplicate applications
    if (lastAppliedAdjustmentIdRef.current === pendingScrollAdjustment.id) {
      DEBUG && console.log('[SimpleScrollList] ⏭️  Skipping duplicate adjustment ID:', pendingScrollAdjustment.id);
      return;
    }

    const container = scrollContainerRef.current;
    const { kind, id, removedCount, prependedCount } = pendingScrollAdjustment;

    // ── ANCHOR CORRECTION (preferred path) ──────────────────────────────────
    //
    // We are inside a layout effect, so React has already committed the new
    // item list to the DOM but the browser has not painted yet. If the row we
    // were anchored to still exists, we can measure precisely how far it moved
    // and cancel exactly that — no height estimates, no accumulated error, and
    // it works identically for prepend and removeTop.
    const anchorId = anchorIdRef.current;
    if (anchorId) {
      // Match on data-render-key, not data-event-id: when the pool is exhausted
      // the feed replays the same events, so several rows share one event id and
      // only __renderKey identifies a specific copy. Scanning and comparing
      // avoids any attribute-escaping concerns with generated keys.
      let anchorEl = null;
      const candidates = container.querySelectorAll('[data-render-key]');
      for (let i = 0; i < candidates.length; i++) {
        if (candidates[i].getAttribute('data-render-key') === anchorId) {
          anchorEl = candidates[i];
          break;
        }
      }
      if (anchorEl) {
        const containerTop = container.getBoundingClientRect().top;
        const newTop = anchorEl.getBoundingClientRect().top - containerTop;

        // Where the anchor would be RIGHT NOW if the DOM had not changed at
        // all: its captured offset, carried by however far the user has
        // scrolled since. Anything beyond this is the content moving under
        // them, which is the only thing worth compensating.
        const userScrolledSinceCapture = container.scrollTop - anchorScrollTopRef.current;
        const expectedTop = anchorTopRef.current - userScrolledSinceCapture;
        const drift = newTop - expectedTop;

        if (Math.abs(drift) > 0.5) {
          scrollAdjustmentInProgressRef.current = true;
          const r = setScrollTopInstant(container, container.scrollTop + drift);
          trace('compensate/anchor', kind, 'id=' + anchorId,
            'drift=' + Math.round(drift),
            'userScroll=' + Math.round(userScrolledSinceCapture),
            'scrollTop', Math.round(r.before), '->', Math.round(r.after),
            'applied=' + Math.round(r.applied),
            r.clamped
              ? '⚠️CLAMPED wanted=' + Math.round(r.wanted) + ' max=' + Math.round(r.maxScroll)
              : '');
          if (r.clamped) {
            // The content is now too short to hold the corrected position. With
            // a constant-size window this should not happen; if it does, the
            // window shrank and that is the bug to chase, not this write.
            console.warn('[SimpleScrollList] ⚠️ Scroll compensation clamped — content height collapsed?', {
              wanted: Math.round(r.wanted),
              maxScroll: Math.round(r.maxScroll),
              scrollHeight: Math.round(container.scrollHeight),
              rows: Array.isArray(places) ? places.length : -1,
            });
          }
          requestAnimationFrame(() => { scrollAdjustmentInProgressRef.current = false; });
        } else {
          trace('compensate/anchor', kind, 'no drift');
        }

        lastAppliedAdjustmentIdRef.current = id;
        if (typeof pendingScrollAdjustment.onApplied === 'function') {
          pendingScrollAdjustment.onApplied(id);
        }
        return;
      }
      trace('compensate/anchor MISS', 'id=' + anchorId, '→ falling back to height sum');
    }

    if (kind === 'removeTop') {
      // Always compensate - don't try to be smart
      DEBUG && console.log('[SimpleScrollList] 🔧 REMOVE_TOP: compensating for', removedCount, 'items');

      // Find the removed items from previousItemsRef
      const removedItems = previousItemsRef.current.slice(0, removedCount);
      DEBUG && console.log('[SimpleScrollList] 📦 Removed items:', removedItems.map(i => i.event_id || i.id));

      // Calculate average height from cache for FALLBACK when cache misses
      const cachedHeights = Array.from(itemHeightCacheRef.current.values());
      const averageHeight = cachedHeights.length > 0
        ? cachedHeights.reduce((a, b) => a + b, 0) / cachedHeights.length
        : 150; // Fallback to 150px if no cache at all

      DEBUG && console.log('[SimpleScrollList] 📊 Average height:', Math.round(averageHeight), 'px from', cachedHeights.length, 'cached items');

      // Sum heights from height map with fallback to average
      let delta = 0;
      let cacheMisses = 0;
      removedItems.forEach((item) => {
        const eventId = String(item.event_id || item.id);
        const isCached = itemHeightCacheRef.current.has(eventId);
        const height = isCached ? itemHeightCacheRef.current.get(eventId) : averageHeight;

        if (!isCached) {
          cacheMisses++;
        }

        delta += height;
        DEBUG && console.log('[SimpleScrollList] 📏', eventId, '→',
          isCached ? 'CACHED' : '⚠️ FALLBACK',
          Math.round(height), 'px');
      });

      if (cacheMisses > 0) {
        DEBUG && console.log('[SimpleScrollList] ⚠️ Cache misses:', cacheMisses, '/', removedItems.length, '- using average height fallback');
      }

      if (delta > 0) {
        // Mark as programmatic scroll
        scrollAdjustmentInProgressRef.current = true;

        const r = setScrollTopInstant(container, Math.max(0, container.scrollTop - delta));
        trace('compensate/heightsum removeTop',
          'delta=' + Math.round(delta),
          'scrollTop', Math.round(r.before), '->', Math.round(r.after),
          r.clamped ? '⚠️CLAMPED max=' + Math.round(r.maxScroll) : '');
        DEBUG && console.log('[SimpleScrollList] ✅ Compensated:', Math.round(r.before), '→', Math.round(r.after), '(delta:', Math.round(delta), 'px)');

        // INSTAGRAM-STYLE: Re-enable immediately after compensation
        setTimeout(() => {
          scrollAdjustmentInProgressRef.current = false;
        }, 30); // Reduced from 100ms to 30ms
      } else {
        // No compensation needed, clear flag immediately
        scrollAdjustmentInProgressRef.current = false;
      }

      lastAppliedAdjustmentIdRef.current = id;
    } else if (kind === 'prepend') {
      // Items added at top → scroll down to compensate
      DEBUG && console.log('[SimpleScrollList] 🔧 PREPEND: compensating for', prependedCount, 'items');

      // Find the prepended items (they're now at the start of places)
      const prependedItems = places.slice(0, prependedCount);
      DEBUG && console.log('[SimpleScrollList] 📦 Prepended items:', prependedItems.map(i => i.event_id || i.id));

      // BUGFIX (runaway load loop): this used to be `... .get(eventId) || 0`
      // with no fallback. Prepended items have by definition never been on
      // screen, so they are never in the height cache — delta was ALWAYS 0 and
      // the `if (delta > 0)` below meant a prepend was never compensated at all.
      //
      // The consequence was a self-sustaining loop: sitting near the top fires
      // onEdgeDemand('up') → loadPrevious prepends 15 items → no compensation,
      // so the viewport is still pinned at the top → fires 'up' again →
      // ... which is the list "wildly scrolling up and down" and the storm of
      // wrap_refresh fetches and duplicate-filter warnings in the trace.
      //
      // removeTop already had an average-height fallback; prepend needs the
      // same one.
      const cachedHeights = Array.from(itemHeightCacheRef.current.values());
      const averageHeight = cachedHeights.length > 0
        ? cachedHeights.reduce((a, b) => a + b, 0) / cachedHeights.length
        : 150;

      let delta = 0;
      prependedItems.forEach((item) => {
        const eventId = String(item.event_id || item.id);
        const height = itemHeightCacheRef.current.get(eventId) ?? averageHeight;
        delta += height;
      });

      DEBUG && console.log('[SimpleScrollList] 📊 Prepend delta:', Math.round(delta),
        'px (avg', Math.round(averageHeight), 'px from', cachedHeights.length, 'cached)');

      if (delta > 0) {
        scrollAdjustmentInProgressRef.current = true;

        const r = setScrollTopInstant(container, container.scrollTop + delta);
        trace('compensate/heightsum prepend',
          'delta=' + Math.round(delta),
          'scrollTop', Math.round(r.before), '->', Math.round(r.after),
          r.clamped ? '⚠️CLAMPED max=' + Math.round(r.maxScroll) : '');
        DEBUG && console.log('[SimpleScrollList] ✅ Compensated:', Math.round(r.before), '→', Math.round(r.after), '(delta:', Math.round(delta), 'px)');

        // INSTAGRAM-STYLE: Re-enable immediately after compensation
        setTimeout(() => {
          scrollAdjustmentInProgressRef.current = false;
        }, 30); // Reduced from 100ms to 30ms
      } else {
        // No compensation needed, clear flag immediately
        scrollAdjustmentInProgressRef.current = false;
      }

      lastAppliedAdjustmentIdRef.current = id;
    }

    // Tell parent we're done - use the callback from adjustment object
    if (typeof pendingScrollAdjustment.onApplied === 'function') {
      pendingScrollAdjustment.onApplied(id);
      DEBUG && console.log('[SimpleScrollList] ✅ Scroll compensation callback fired for adjustment', id);
    }
  }, [pendingScrollAdjustment, places]);

  // Cleanup cache on unmount
  useEffect(() => {
    return () => {
      itemHeightCacheRef.current.clear();
      DEBUG && console.log('[SimpleScrollList] 🧹 Cleaned up height cache');
    };
  }, []);

  /**
   * PERFORMANCE: Debounced parent notification
   * We separate "detecting the centered item" from "notifying the parent"
   * to avoid triggering expensive re-renders on every scroll frame
   */
  const notifyParentIndexChange = useRef(
    debounce((index) => {
      // Only notify if index actually changed from last reported value
      if (index !== lastReportedIndexRef.current && onActiveIndexChange) {
        onActiveIndexChange(index);
        lastReportedIndexRef.current = index;
      }
    }, 200) // 200ms debounce - waits for scrolling to settle
  ).current;

  const incrementItemViewCount = useCallback(async (item) => {
    const isReview = item._source === 'review';
    const id = parseInt(item.id, 10);
    const type = isReview ? 'review' : 'event';
    if (!id || isNaN(id)) return;
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/views/increment`, { type, id });
    } catch (err) {
      console.warn('[ViewCount] increment failed:', err.message);
    }
  }, []);

  /**
   * Find centered card - simplified version
   * PERFORMANCE: Uses requestAnimationFrame + debounced parent notification
   * CRITICAL: Includes edge detection to trigger loading more items
   */
  const findCenteredCard = useCallback(() => {
    // Guard: skip if no valid data or currently adjusting scroll
    if (!scrollContainerRef.current || !elRefs.length || !Array.isArray(places)) return;
    if (isScrollingFromClickRef.current || scrollAdjustmentInProgressRef.current) return;

    // Block during window shifts
    if (isShiftingWindow) {
      DEBUG && console.log('[SimpleScrollList] ⏸️  findCenteredCard blocked during window shift');
      return;
    }

    // Use requestAnimationFrame to batch DOM reads
    requestAnimationFrame(() => {
      if (!scrollContainerRef.current || !elRefs.length) return;

      const container = scrollContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.top + containerRect.height / 2;

      let closestIndex = 0;
      let minDistance = Infinity;
      // Anchor bookkeeping: the first row whose bottom is still below the top
      // of the viewport, i.e. the topmost visible row. Recorded on every pass
      // so a window shift always has a fresh reference point to correct
      // against. See applyAnchorCorrection().
      let anchorId = null;
      let anchorTop = 0;

      // Find the centered card
      elRefs.forEach((ref, index) => {
        if (ref?.current && places[index] && !places[index].__skeleton) {
          const elementRect = ref.current.getBoundingClientRect();
          const elementCenter = elementRect.top + elementRect.height / 2;
          const distance = Math.abs(elementCenter - containerCenter);

          if (distance < minDistance) {
            minDistance = distance;
            closestIndex = index;
          }

          const relTop = elementRect.top - containerRect.top;
          if (anchorId === null && elementRect.bottom > containerRect.top) {
            anchorId = String(places[index].__renderKey || places[index].event_id || places[index].id);
            anchorTop = relTop;
          }
        }
      });

      if (anchorId !== null) {
        anchorIdRef.current = anchorId;
        anchorTopRef.current = anchorTop;
        // Stamp the scroll position this measurement is relative to, so drift
        // can later be separated from the user's own subsequent scrolling.
        anchorScrollTopRef.current = container.scrollTop;
      }

      // NOTE: edge detection deliberately does NOT live here any more.
      //
      // SimpleHorizontalList has exactly one edge check, in its scroll handler.
      // This component had two independent ones — an index-based check here and
      // a position-based check in handleScroll — each with its own copy of the
      // cooldown. They fired against each other and doubled the request rate.
      // findCenteredCard's only job now is working out which card is centred.

      // Update if changed
      const hasChanged = closestIndex !== activeIndex &&
                        places[closestIndex] &&
                        !places[closestIndex].__skeleton;

      // Don't report a focus change that we didn't get from the user moving.
      // Right after a window shift the content slides under a stationary
      // viewport, so findCenteredCard legitimately finds a *different* card
      // without anyone scrolling — reporting that upward is what let a shift
      // provoke the next shift. The rail guards the same way with its
      // post-shift cooldown and clamp.
      if (windowShiftCooldownRef.current) {
        DEBUG && console.log('[SimpleScrollList] ⏸️ Focus report suppressed during post-shift cooldown');
        return;
      }

      if (hasChanged) {
        setActiveIndex(closestIndex);
        const currentItem = places[closestIndex];

        // Report to parent
        if (onActiveIndexChange && currentItem && !currentItem.__skeleton) {
          trace('FOCUS report', activeIndex, '->', closestIndex,
            'id=' + (currentItem.__renderKey || currentItem.event_id || currentItem.id));
          onActiveIndexChange(closestIndex, currentItem);
        }

        // Start 3-second dwell timer for the newly centered item
        if (dwellTimerRef.current) {
          clearTimeout(dwellTimerRef.current);
          dwellTimerRef.current = null;
        }
        const centeredItem = places[closestIndex];
        if (centeredItem && !centeredItem.__skeleton) {
          dwellTimerRef.current = setTimeout(() => {
            dwellTimerRef.current = null;
            incrementItemViewCount(centeredItem);
          }, 3000);
        }
      }
    });
    // onEdgeDemand / isLoading* are intentionally absent: this callback no
    // longer performs edge detection, so depending on them would only churn
    // the scroll listener.
  }, [elRefs, places, activeIndex, tracker, onActiveIndexChange, isShiftingWindow, incrementItemViewCount]);

  /**
   * Single edge-detection entry point.
   *
   * Callable both from the scroll handler and directly from gestures. That
   * second path matters: once the user is pinned at the very bottom, scrollTop
   * can no longer change, so the browser stops emitting `scroll` events
   * entirely. A purely scroll-driven detector goes deaf at exactly the moment
   * the user is asking for more — which is the "reached an item that cannot
   * scroll further" dead end. Wheel/touchmove keep firing there, so we re-check
   * from those too.
   *
   * Three ways an edge may fire:
   *   1. Fresh entry into the zone (the latch — normal case).
   *   2. Real progress since the last request (the content grew, or the user
   *      travelled at least half a viewport), meaning this is a new approach.
   *   3. Stuck retry — still in the same zone STUCK_RETRY_MS after the last
   *      request, so that request clearly achieved nothing. This is the
   *      guarantee that the feed can never permanently dead-end.
   */
  const requestEdgeIfNeeded = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || typeof onEdgeDemand !== 'function') return;
    if (isShiftingWindow || windowShiftCooldownRef.current) return;
    if (isLoadingNext || isLoadingPrevious) return;
    if (isScrollingFromClickRef.current || scrollAdjustmentInProgressRef.current) return;

    const now = Date.now();
    // Prefetching stays gesture-driven; a scroll we caused ourselves must never
    // be able to request data (that was the original runaway loop).
    if (now >= userIntentUntilRef.current) return;

    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const scrollBottom = scrollHeight - (scrollTop + clientHeight);

    // Fast scroll = reach further ahead, so the next batch is ready on arrival.
    const velocityMultiplier = Math.min(3, Math.max(1, 1 + scrollVelocityRef.current * 2));

    /**
     * DESKTOP FIX: the base threshold used to be a flat 600px.
     *
     * 600px is about one full screen on the mobile list (viewport ~590px), so
     * the request went out roughly a screen before the end — enough warning for
     * the load to land. In the desktop sidebar the viewport is the full window
     * height (~900px and up), so the same 600px is only two thirds of a screen:
     * the user reaches the hard bottom well before the batch arrives, and the
     * feed is slow enough for that to matter — the wrap/retry chain in the trace
     * runs three sequential requests and takes the better part of a second.
     *
     * Scaling with the viewport gives every screen size the same amount of
     * warning: one full screen, and never less than the old 600px, so the
     * mobile behaviour that was just verified is unchanged.
     */
    const baseThreshold = Math.max(600, clientHeight);
    const edgeThreshold = Math.round(baseThreshold * velocityMultiplier);

    const nearBottom = scrollBottom < edgeThreshold && scrollBottom <= scrollTop;
    const nearTop = !nearBottom && scrollTop < edgeThreshold;

    const grew = scrollHeight !== lastDemandHeightRef.current;
    const travelled = Math.abs(scrollTop - lastDemandTopRef.current) > clientHeight / 2;
    const stuck = now - lastEdgeDemandTimeRef.current > STUCK_RETRY_MS;

    const fire = (direction, why) => {
      trace('EDGE', direction, why,
        'top=' + Math.round(scrollTop),
        'bottom=' + Math.round(scrollBottom),
        'thr=' + edgeThreshold,
        'h=' + Math.round(scrollHeight));
      onEdgeDemand(direction);
      edgeStateRef.current = direction;
      lastEdgeDemandTimeRef.current = now;
      lastDemandHeightRef.current = scrollHeight;
      lastDemandTopRef.current = scrollTop;
    };

    if (nearBottom) {
      if (edgeStateRef.current !== 'down') fire('down', '(entered zone)');
      else if (grew || travelled) fire('down', '(new approach)');
      else if (stuck) fire('down', '(stuck retry)');
    } else if (nearTop) {
      if (edgeStateRef.current !== 'up') fire('up', '(entered zone)');
      else if (grew || travelled) fire('up', '(new approach)');
      else if (stuck) fire('up', '(stuck retry)');
    } else if (edgeStateRef.current !== null) {
      // Back in the middle — re-arm both edges.
      edgeStateRef.current = null;
    }
  }, [onEdgeDemand, isShiftingWindow, isLoadingNext, isLoadingPrevious]);

  // Stable handle so the gesture listeners can call the latest version without
  // being torn down and re-attached on every render.
  const requestEdgeRef = useRef(requestEdgeIfNeeded);
  useEffect(() => { requestEdgeRef.current = requestEdgeIfNeeded; }, [requestEdgeIfNeeded]);

  /**
   * PERFORMANCE: Throttled scroll handler
   * Tracks user scrolling state and notifies parent when scrolling starts/stops
   * OPTIMIZED: Reduced guards for smoother scrolling on mobile
   * CRITICAL: Includes scroll position-based edge detection
   */
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    // CRITICAL: Only ignore during actual compensation, not during all shifts
    // This allows smoother scrolling by reducing blocked scroll events
    if (scrollAdjustmentInProgressRef.current) {
      DEBUG && console.log('[SimpleScrollList] ⏸️  Ignoring scroll during compensation');
      return;
    }

    // Ported from the rail's `isUserNavigatingRef` early-return: a scroll we
    // started ourselves (the mount restore, or a map-marker scrollIntoView) is
    // not information about where the user wants to be. Letting it through also
    // poisoned the velocity estimate, which inflates the edge threshold up to
    // 3x and made the edge zones swallow most of the list.
    if (isScrollingFromClickRef.current) {
      DEBUG && console.log('[SimpleScrollList] ⏸️  Ignoring scroll during programmatic navigation');
      return;
    }

    const container = scrollContainerRef.current;
    const scrollTop = container.scrollTop;

    // Which way did the user just move? Needed for the latch release below.
    const movingUp = scrollTop < lastScrollTopRef.current - 1;
    const movingDown = scrollTop > lastScrollTopRef.current + 1;

    // INSTAGRAM-STYLE: Calculate scroll velocity for dynamic prefetching
    const now = Date.now();
    const scrollDelta = Math.abs(scrollTop - lastScrollTopRef.current);
    const timeDelta = now - lastScrollTimeRef.current;

    if (timeDelta > 0) {
      // Velocity in pixels per millisecond
      scrollVelocityRef.current = scrollDelta / timeDelta;
    }

    trace('scroll', 'top=' + Math.round(scrollTop),
      'd=' + (scrollTop - lastScrollTopRef.current > 0 ? '+' : '') + Math.round(scrollTop - lastScrollTopRef.current),
      'h=' + Math.round(container.scrollHeight),
      'v=' + scrollVelocityRef.current.toFixed(2),
      'edge=' + edgeStateRef.current,
      'intent=' + (now < userIntentUntilRef.current ? 'Y' : 'N'));

    lastScrollTopRef.current = scrollTop;
    lastScrollTimeRef.current = now;

    // Notify parent that scrolling has started
    if (onScrollStart) {
      onScrollStart();
    }

    // INSTAGRAM-STYLE POSITION-BASED DETECTION with velocity-based prefetching
    // Fast scroll = load earlier (3x threshold), slow scroll = normal threshold
    // Same gating as findCenteredCard: a real gesture, off cooldown, and no
    // load already in flight. Without the user-intent check this branch was the
    // main engine of the runaway loop — it fires on *any* scrollTop change, and
    // prepend compensation changes scrollTop by design.
    // Direction-reversal release, ported from the rail.
    //
    // The rail already learned this the hard way: latching on 'down' until the
    // user scrolls all the way back to the middle means that if one load
    // fails, the user is parked at the last item with the latch stuck and no
    // further request is ever made — a permanent dead end. Any movement away
    // from the latched edge means they are no longer pinned there, so release
    // it and let the next approach re-arm.
    if (movingUp && edgeStateRef.current === 'down') edgeStateRef.current = null;
    if (movingDown && edgeStateRef.current === 'up') edgeStateRef.current = null;

    requestEdgeIfNeeded();

    // Throttled: find which card is centered
    // Only skip if actively shifting (not just loading)
    if (!isShiftingWindow) {
      findCenteredCard();
    }

    // Clear user scrolling flag after inactivity
    clearTimeout(window._scrollInactivityTimer);
    window._scrollInactivityTimer = setTimeout(() => {
      if (onScrollEnd) {
        onScrollEnd();
      }
    }, 200);
  }, [findCenteredCard, onScrollStart, onScrollEnd, isShiftingWindow, requestEdgeIfNeeded]);

  /**
   * PERFORMANCE: Set up scroll listeners with throttling
   * We use 100ms throttle (10fps) for better performance on mobile
   * Plus a debounced "scroll end" handler for final accuracy
   */
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !onActiveIndexChange) return;

    // Throttled scroll handler (100ms = 10fps, good balance for mobile)
    const throttledHandler = throttle(handleScroll, 100);

    // Debounced scroll end handler for final accuracy (150ms after last scroll)
    let scrollEndTimeout;
    const scrollEndHandler = () => {
      clearTimeout(scrollEndTimeout);
      scrollEndTimeout = setTimeout(() => {
        handleScroll();
      }, 150);
    };

    container.addEventListener('scroll', throttledHandler, { passive: true });
    container.addEventListener('scroll', scrollEndHandler, { passive: true });

    return () => {
      container.removeEventListener('scroll', throttledHandler);
      container.removeEventListener('scroll', scrollEndHandler);
      clearTimeout(scrollEndTimeout);
      if (window._scrollInactivityTimer) {
        clearTimeout(window._scrollInactivityTimer);
      }
    };
  }, [handleScroll, onActiveIndexChange]);

  /**
   * Safety net for the mount restore: if no usable selection has arrived within
   * a second of mounting, stop waiting so that a later map-marker tap animates
   * normally instead of being swallowed as "the restore".
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!initialRestoreDoneRef.current) {
        initialRestoreDoneRef.current = true;
        DEBUG && console.log('[SimpleScrollList] ⏱ Mount restore window closed with no selection');
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  /**
   * ONE-SHOT MOUNT RESTORE — hand-off from the แผนที่ tab.
   *
   * Switching tabs keeps a single SmartEventList alive and only swaps the leaf
   * list component, so `items` and the focus index survive; it is this
   * component that mounts from scratch, at scrollTop 0 with no activeIndex.
   * `childClicked` is already the index of whatever card was centred in the
   * other tab (both lists report their centre up through
   * SmartEventList.handleListActiveIndexChange → setChildClicked), so the
   * correct opening position is simply "centre childClicked".
   *
   * Two things make this a separate effect rather than a tweak to the one below:
   *
   *  1. It must be INSTANT. Reaching the restore position through the normal
   *     smooth `scrollIntoView` is what produced the long unexplained glide
   *     down the list on open — the user should never see the intermediate
   *     cards, the list should simply already be there.
   *  2. It must WAIT for refs. `elRefs` is populated by an effect after the
   *     first render, so on the very first pass `elRefs[childClicked]` is
   *     undefined. Bailing out early (as the previous mount guard did) is what
   *     made the list open at the top instead. This effect re-runs as `elRefs`
   *     and `places` settle and fires exactly once, when the target exists.
   */
  useEffect(() => {
    if (initialRestoreDoneRef.current) return;

    // The user got here first (they scrolled before we were ready). Their
    // position wins; stand down permanently.
    if (hasUserInteractedRef.current) {
      initialRestoreDoneRef.current = true;
      return;
    }

    if (!Array.isArray(places) || places.length === 0) return;

    // BUGFIX: this used to mark the restore *done* when childClicked was still
    // null, which on a normal tab switch is simply the state of the very first
    // pass — the prop arrives a render later. That permanently cancelled the
    // restore and left the list parked at scrollTop 0, which is what put it
    // inside the top edge-demand zone and kicked off the load loop. Keep
    // waiting instead; the safety timer below settles the case where no
    // selection ever arrives.
    if (childClicked === null || childClicked === undefined) return;

    const target = elRefs[childClicked]?.current;
    if (!target) return; // refs not attached yet — try again on the next render

    initialRestoreDoneRef.current = true;
    lastChildClickedRef.current = childClicked;

    // Seed our own focus state to the restored card. Without this the first
    // findCenteredCard() after the jump would treat the restored position as a
    // brand-new user selection and report it back up, which round-trips as a
    // fresh childClicked and re-triggers scrolling.
    setActiveIndex(childClicked);
    lastReportedIndexRef.current = childClicked;

    // Swallow the scroll events the jump generates.
    isScrollingFromClickRef.current = true;
    // BUGFIX: this said `behavior: 'auto'`, which does NOT mean instant — it
    // means "defer to the CSS scroll-behavior", and the container was set to
    // smooth. So the restore this comment insists must be instant was in fact
    // animating the whole way down the list, which is the long unexplained
    // glide on open. 'instant' is unconditional.
    target.scrollIntoView({ behavior: 'instant', block: 'center', inline: 'nearest' });
    trace('RESTORE to index', childClicked,
      'scrollTop=' + Math.round(scrollContainerRef.current?.scrollTop ?? -1),
      'items=' + places.length);

    // Long enough to outlast the 100ms scroll throttle and the 150ms
    // scroll-end debounce, short enough that a user who starts scrolling
    // straight away is not ignored.
    setTimeout(() => {
      isScrollingFromClickRef.current = false;
    }, 300);
  }, [childClicked, elRefs, places]);

  /**
   * Scroll to item when clicked from map
   * ENHANCED: Respects window shifting + cooldown + user scrolling + all loading states
   * CRITICAL: Ignores self-originated selection to prevent feedback loop
   */
  useEffect(() => {
    // The one-shot mount restore above owns the first positioning pass. Until
    // it has run, this effect must stay out of the way or the two will fight.
    if (!initialRestoreDoneRef.current) return;

    if (childClicked === null ||
        childClicked === lastChildClickedRef.current ||
        isScrollingFromClickRef.current ||
        scrollAdjustmentInProgressRef.current ||
        isShiftingWindow || // ← BLOCK during window shifts
        windowShiftCooldownRef.current || // ← BLOCK during cooldown
        userScrollingRef.current || // ← BLOCK when user is scrolling
        isLoadingNext || // ← BLOCK during next loading
        isLoadingPrevious || // ← BLOCK during previous loading
        !elRefs[childClicked]?.current) {
      return;
    }

    // CRITICAL: If the parent is just echoing back what we already scrolled to, ignore it
    // This prevents the feedback loop: scroll → onActiveIndexChange → parent updates state →
    // childClicked changes → scrollIntoView → ruins free scrolling
    if (childClicked === activeIndex) {
      DEBUG && console.log('🎯 [SimpleScrollList] Ignoring childClicked - already at activeIndex:', activeIndex);
      return;
    }

    DEBUG && console.log('🎯 [SimpleScrollList] Scrolling to item:', childClicked);

    isScrollingFromClickRef.current = true;
    lastChildClickedRef.current = childClicked;

    requestAnimationFrame(() => {
      if (elRefs[childClicked]?.current) {
        elRefs[childClicked].current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });

        setTimeout(() => {
          isScrollingFromClickRef.current = false;
        }, 1200);
      } else {
        isScrollingFromClickRef.current = false;
      }
    });
  }, [childClicked, elRefs, isShiftingWindow, isLoadingNext, isLoadingPrevious, activeIndex]);

  /**
   * NOTE: We removed the duplicate isShiftingWindow effect that was unlocking immediately.
   * Lock/unlock is now handled in the main effect (lines 313-361) which properly waits
   * for compensation verification before unlocking.
   */

  /**
   * NOTE: We removed scroll position preservation during shifts
   * The removeTop compensation is sufficient and preserving scroll was fighting it
   */

  const getEmptyMessage = () => {
    return emptyMessages[language] || emptyMessages.EN;
  };

  const validPlaces = Array.isArray(places) ? places : [];

  const handleItemClick = useCallback((place, index) => {
    if (!place.__skeleton && tracker) {
      tracker.trackInteraction({
        eventId: place.id || place.event_id,
        action: 'click_event_card',
        context: 'scroll_list',
        position: index,
        timestamp: Date.now(),
      });
    }
  }, [tracker]);

  return (
    <StyledContainer>
      {isLoading ? (
        <LoadingContainer>
          <CircularProgress size={50} />
        </LoadingContainer>
      ) : (
        <ScrollableGrid
          ref={scrollContainerRef}
          container
          spacing={0}
          sx={{
            // MOBILE OPTIMIZATIONS: Better touch handling
            touchAction: 'pan-y',
            // NO `scrollBehavior: 'smooth'` here — see setScrollTopInstant().
            // CSS smooth scrolling turns every scrollTop assignment into an
            // animation, which silently defeated scroll compensation (the
            // write appeared to do nothing) and left an animation running long
            // after we had stopped treating the scroll as programmatic.
            // The one place smooth motion IS wanted — scrolling to a card
            // tapped on the map — asks for it explicitly via
            // scrollIntoView({ behavior: 'smooth' }), which works regardless.
            // Reduce overscroll bounce on iOS
            overscrollBehavior: 'contain',
            // Keep overflow: auto to allow scroll detection, but prevent visual jumps via scroll restoration
          }}
        >
          {validPlaces.length > 0 ? (
            validPlaces.map((place, i) => (
              <VerticalEventRow
                key={place.__renderKey || place.event_id || place.id || `place-${i}`}
                place={place}
                index={i}
                isActive={Number(childClicked) === i}
                elRef={elRefs[i]}
                onClick={handleItemClick}
                language={language}
                onLocationClick={onLocationClick}
                // BUGFIX (third competing scroller): PlaceDetails runs
                //   if (selected && refProp?.current && !suppressAutoScroll && !isShiftingWindow)
                //     refProp.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                // in its *render body*, so the selected row re-issued a smooth
                // scroll on every single re-render of that row. On mount that
                // row is far below the fold, so `block: 'nearest'` really does
                // scroll — an unguarded smooth animation racing this
                // component's own positioning, with no awareness of whether the
                // user is currently dragging the list.
                //
                // SimpleScrollList owns scroll position here (mount restore +
                // the childClicked effect, both properly guarded against user
                // scrolling, window shifts and in-flight loads), so PlaceDetails
                // must not also try. The parent's `suppressAutoScroll`
                // (localShifting) is subsumed by this.
                suppressAutoScroll={true}
                isShiftingWindow={isShiftingWindow}
                onExpand={onExpand}
                onCreatorClick={onCreatorClick}
              />
            ))
          ) : (
            <Box sx={{ width: '100%', textAlign: 'center', padding: 2 }}>
              <Typography>
                {hasSearched ? getEmptyMessage() : (
                  language === 'TH' ? 'กำลังโหลด...' :
                  language === 'CN' ? '加载中...' :
                  'Loading...'
                )}
              </Typography>
            </Box>
          )}
        </ScrollableGrid>
      )}

      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </StyledContainer>
  );
};

// STEP 1: Remove outer React.memo - export component directly
// This allows the component to re-render when isShiftingWindow changes
export default SimpleScrollList;